import { Suspense, useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import { LATTICES, buildSupercell, type Lattice } from "@/data/crystals";

function Atom({
  position,
  color,
  radius,
  label,
  showLabel,
}: {
  position: [number, number, number];
  color: string;
  radius: number;
  label?: string;
  showLabel?: boolean;
}) {
  return (
    <group position={position}>
      <mesh>
        <sphereGeometry args={[radius, 24, 24]} />
        <meshStandardMaterial color={color} roughness={0.4} metalness={0.1} />
      </mesh>
      {showLabel && label && (
        <Html center distanceFactor={6} style={{ pointerEvents: "none" }}>
          <span className="text-[10px] font-mono text-foreground bg-background/70 px-1 rounded whitespace-nowrap">
            {label}
          </span>
        </Html>
      )}
    </group>
  );
}

function Bond({
  a,
  b,
}: {
  a: [number, number, number];
  b: [number, number, number];
}) {
  const { position, quaternion, length } = useMemo(() => {
    const start = new THREE.Vector3(...a);
    const end = new THREE.Vector3(...b);
    const dir = new THREE.Vector3().subVectors(end, start);
    const len = dir.length();
    const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    const up = new THREE.Vector3(0, 1, 0);
    const q = new THREE.Quaternion().setFromUnitVectors(up, dir.clone().normalize());
    return {
      position: mid.toArray() as [number, number, number],
      quaternion: [q.x, q.y, q.z, q.w] as [number, number, number, number],
      length: len,
    };
  }, [a, b]);
  return (
    <mesh position={position} quaternion={quaternion}>
      <cylinderGeometry args={[0.04, 0.04, length, 12]} />
      <meshStandardMaterial color="hsl(220 13% 55%)" roughness={0.6} />
    </mesh>
  );
}

function ElectronSea({ extent }: { extent: number }) {
  // 200 small drifting blue dots
  const dots = useMemo(() => {
    const arr: [number, number, number][] = [];
    for (let i = 0; i < 250; i++) {
      arr.push([
        (Math.random() - 0.5) * extent,
        (Math.random() - 0.5) * extent,
        (Math.random() - 0.5) * extent,
      ]);
    }
    return arr;
  }, [extent]);
  return (
    <group>
      {dots.map((p, i) => (
        <mesh key={i} position={p}>
          <sphereGeometry args={[0.04, 6, 6]} />
          <meshBasicMaterial color="hsl(210 90% 65%)" transparent opacity={0.55} />
        </mesh>
      ))}
    </group>
  );
}

function LatticeScene({
  lattice,
  cells,
  showBonds,
  showLabels,
  showSea,
}: {
  lattice: Lattice;
  cells: number;
  showBonds: boolean;
  showLabels: boolean;
  showSea: boolean;
}) {
  const atoms = useMemo(() => buildSupercell(lattice, cells), [lattice, cells]);

  // Bonds only meaningful for single unit cell (avoid combinatorial explosion)
  const bondPairs =
    showBonds && cells === 1
      ? lattice.bonds.map((b) => ({
          a: atoms[b.a]?.pos as [number, number, number],
          b: atoms[b.b]?.pos as [number, number, number],
        }))
      : [];

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 8, 5]} intensity={0.8} />
      <directionalLight position={[-5, -3, -5]} intensity={0.3} />
      {atoms.map((a, i) => (
        <Atom
          key={i}
          position={a.pos}
          color={a.kind.color}
          radius={a.kind.radius}
          label={a.kind.symbol}
          showLabel={showLabels && cells === 1 && i < lattice.atoms.length}
        />
      ))}
      {bondPairs.map((p, i) =>
        p.a && p.b ? <Bond key={i} a={p.a} b={p.b} /> : null,
      )}
      {showSea && lattice.hasElectronSea && <ElectronSea extent={cells * lattice.a} />}
      <OrbitControls enablePan={false} />
    </>
  );
}

export default function CrystalLattices() {
  const [activeKey, setActiveKey] = useState(LATTICES[0].key);
  const lattice = LATTICES.find((l) => l.key === activeKey)!;
  const [cells, setCells] = useState(1);
  const [showBonds, setShowBonds] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showSea, setShowSea] = useState(true);

  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader />
      <Main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Crystal Lattice Explorer</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Compare ionic, covalent network, metallic, and molecular solids in 3D — and see why
            structure dictates property.
          </p>
        </div>

        {/* Lattice tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {LATTICES.map((l) => (
            <button
              key={l.key}
              onClick={() => setActiveKey(l.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                activeKey === l.key
                  ? "bg-foreground text-background"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {l.name} <span className="opacity-60 ml-1">· {l.type}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
          <div className="rounded-lg border border-border bg-tile-bg overflow-hidden h-[480px]">
            <Canvas camera={{ position: [2.5, 2.2, 2.8], fov: 45 }}>
              <Suspense fallback={null}>
                <LatticeScene
                  lattice={lattice}
                  cells={cells}
                  showBonds={showBonds}
                  showLabels={showLabels}
                  showSea={showSea}
                />
              </Suspense>
            </Canvas>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-tile-bg p-3">
              <h2 className="text-sm font-semibold text-foreground mb-2">{lattice.name}</h2>
              <p className="text-xs font-mono text-muted-foreground mb-3">
                Type: <span className="text-foreground capitalize">{lattice.type}</span> · Formula:{" "}
                <span className="text-foreground">{lattice.formula}</span>
              </p>
              <p className="text-xs text-foreground leading-relaxed mb-3">{lattice.insight}</p>
              <div className="rounded-md bg-primary/5 border border-primary/30 p-2">
                <p className="text-[11px] uppercase tracking-wider text-primary font-semibold mb-1">
                  Why the property follows
                </p>
                <p className="text-xs text-foreground leading-relaxed">{lattice.property}</p>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-tile-bg p-3 space-y-3">
              <h2 className="text-sm font-semibold text-foreground">View</h2>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Supercell</span>
                  <span className="font-mono text-foreground">
                    {cells}×{cells}×{cells}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={1}
                  value={cells}
                  onChange={(e) => setCells(Number(e.target.value))}
                  className="w-full accent-primary"
                  aria-label="Supercell size"
                />
              </div>
              <Toggle label="Show bonds" value={showBonds} onChange={setShowBonds} />
              <Toggle label="Show labels" value={showLabels} onChange={setShowLabels} />
              {lattice.hasElectronSea && (
                <Toggle label="Electron sea" value={showSea} onChange={setShowSea} />
              )}
              <p className="text-[11px] text-muted-foreground pt-2 border-t border-border">
                Drag to rotate · scroll to zoom
              </p>
            </div>
          </div>
        </div>
      </Main>
    </div>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between text-xs text-foreground cursor-pointer">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-primary"
      />
    </label>
  );
}